import { useEffect, useSyncExternalStore } from 'react';

/**
 * Names for the ids in the address bar.
 *
 * The breadcrumb only sees `/customers/3f2a…`; the page that loaded the record
 * knows it is "Anna Berg". A page renders <BreadcrumbLabel> once it has the
 * name, and the breadcrumb shows that instead of a shortened id.
 */

const labels = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setLabel(id: string, label: string): void {
  if (labels.get(id) === label) return;
  labels.set(id, label);
  version += 1;
  listeners.forEach((listener) => listener());
}

/** The name a page gave this id, or undefined. Re-renders when one arrives. */
export function useBreadcrumbLabel(id: string): string | undefined {
  useSyncExternalStore(subscribe, () => version);
  return labels.get(id);
}

export function BreadcrumbLabel({ id, label }: { id: string; label: string | null | undefined }): null {
  useEffect(() => {
    const name = label?.trim();
    if (id !== '' && name) setLabel(id, name);
  }, [id, label]);
  return null;
}
